/**
 * The Locker's rules (docs/game-design.md §11, Milestone 7): buying cosmetics with coins and
 * wearing them. Pure functions on Progress, used by the game server (which decides every
 * purchase) and by local progression (dev builds). Looks only: nothing here touches stats.
 */
import { COSMETIC_KINDS, cosmeticItem, DEFAULT_FIGHTER, DEFAULT_LOOK, fighterPrice, type CosmeticKind, type Look } from '../config/cosmetics';
import type { Progress } from './profile';

export type LockerError = 'unknownItem' | 'owned' | 'notOwned' | 'locked' | 'notEnoughCoins';

/** How an owned item is stored in Progress.owned. */
export function ownedKey(kind: CosmeticKind, id: string): string {
  return `${kind}:${id}`;
}

export function ownedItems(p: Progress): readonly string[] {
  return p.owned ?? [];
}

/** Free items (the defaults) belong to everyone. */
export function ownsItem(p: Progress, kind: CosmeticKind, id: string): boolean {
  const item = cosmeticItem(kind, id);
  if (!item) return false;
  return item.price === 0 || ownedItems(p).includes(ownedKey(kind, id));
}

/** A look with one item per kind (a look saved before a kind existed gets that kind's default). */
function eachKind(pick: (kind: CosmeticKind) => string): Look {
  const look = { ...DEFAULT_LOOK };
  for (const kind of COSMETIC_KINDS) look[kind] = pick(kind);
  return look;
}

/** The look a player wears: anything unknown or not owned (an old save, a removed item) is the default. */
export function lookOf(p: Progress): Look {
  const look: Partial<Look> = p.look ?? DEFAULT_LOOK;
  return eachKind((kind) => {
    const id = look[kind];
    return id !== undefined && ownsItem(p, kind, id) ? id : DEFAULT_LOOK[kind];
  });
}

/** A look from outside (a match, a replay): unknown items fall back to the defaults. */
export function cleanLook(raw: unknown): Look {
  const o = (raw ?? {}) as Partial<Record<CosmeticKind, unknown>>;
  return eachKind((kind) => {
    const id = o[kind];
    return typeof id === 'string' && cosmeticItem(kind, id) ? id : DEFAULT_LOOK[kind];
  });
}

export interface BuyContext {
  /** The player has fought in the league's top division (Legend): unlocks the Champion effect. */
  legend: boolean;
}

/** Whether an item can't be bought yet, whatever the coins. */
export function itemLocked(kind: CosmeticKind, id: string, ctx: BuyContext): boolean {
  const item = cosmeticItem(kind, id);
  return !!item && 'unlock' in item && item.unlock === 'legend' && !ctx.legend;
}

/** Buys an item with coins and puts it on at once (as the Locker design does). */
export function buyItem(p: Progress, kind: CosmeticKind, id: string, ctx: BuyContext): Progress | LockerError {
  const item = cosmeticItem(kind, id);
  if (!item || !COSMETIC_KINDS.includes(kind)) return 'unknownItem';
  if (ownsItem(p, kind, id)) return 'owned';
  if (itemLocked(kind, id, ctx)) return 'locked';
  if (p.coins < item.price) return 'notEnoughCoins';
  return {
    ...p,
    coins: p.coins - item.price,
    owned: [...ownedItems(p), ownedKey(kind, id)],
    look: { ...lookOf(p), [kind]: id },
  };
}

/** Puts on an item the player owns. */
export function equipItem(p: Progress, kind: CosmeticKind, id: string): Progress | LockerError {
  if (!cosmeticItem(kind, id) || !COSMETIC_KINDS.includes(kind)) return 'unknownItem';
  if (!ownsItem(p, kind, id)) return 'notOwned';
  return { ...p, look: { ...lookOf(p), [kind]: id } };
}

/** How a bought fighter is stored in Progress.owned (D52). */
export function fighterKey(id: string): string {
  return `fighter:${id}`;
}

/** Free fighters belong to everyone; a paid one once bought (D52). */
export function ownsFighter(p: Progress, id: string): boolean {
  return fighterPrice(id) === 0 || ownedItems(p).includes(fighterKey(id));
}

/** The fighter a player fights as: the one they picked if it's theirs, else the default one. */
export function playableFighter(p: Progress, id: string): string {
  return ownsFighter(p, id) ? id : DEFAULT_FIGHTER;
}

/** Buys a paid fighter with coins (D52). Free fighters need no buying. */
export function buyFighter(p: Progress, id: string): Progress | LockerError {
  const price = fighterPrice(id);
  if (!price) return 'unknownItem';
  if (ownsFighter(p, id)) return 'owned';
  if (p.coins < price) return 'notEnoughCoins';
  return { ...p, coins: p.coins - price, owned: [...ownedItems(p), fighterKey(id)] };
}

/**
 * What survives "Reset progress": the Locker (bought fighters too) belongs to the player, not to
 * the fighter's career.
 */
export function keepLocker(from: Progress, into: Progress): Progress {
  return { ...into, owned: [...ownedItems(from)], look: lookOf(from) };
}
